import { createRouter, createWebHistory } from "vue-router";
import { ElNotification } from "element-plus";
import { h } from "vue";
import store from "@/store";

import monitoringRoutes from "../components/monitoring/router";
import { adminRoutes } from "@/admin/routes";
import { courseRoutes } from "@/course/routes";
import { i18n } from "@/main";
import { Route } from "@sentry/vue/types/router";
import { I18n } from "vue-i18n";
import { reportRoutes } from "@/reports/routes";
import { trainerRoutes } from "@/trainer/routes";

const Login = () => import("@/components/auth/AuthPage.vue");
const Home = () => import("@/Home.vue");
const Registration = () => import("@/components/auth/Registration.vue");
const CoursesList = () => import("@/components/courses-list/CoursesList.vue");
const MyCourseList = () => import("@/components/my-course/MyCourseList.vue");
const CompletedList = () => import("@/components/my-course/CompletedList.vue");
const Admin = () => import("@/components/admin/Admin.vue");
const ForgotPassword = () => import("@/components/auth/ForgotPassword.vue");
const NewPassword = () => import("@/components/auth/NewPassword.vue");
const QuizElements = () => import("@/components/quiz/QuizElements.vue");
const QuizStart = () => import("@/components/quiz/QuizStart.vue");
const QuizLesson = () => import("@/components/quiz/QuizLesson.vue");
const QuizQuestion = () => import("@/components/quiz/QuizQuestion.vue");
const QuizChallenge = () => import("@/components/quiz/QuizChallenge.vue");
const QuizQuestionExplanation = () =>
  import("@/components/quiz/QuizQuestionExplanation.vue");
const QuizLessonCompleted = () =>
  import("@/components/quiz/QuizLessonCompleted.vue");
const QuizCompleted = () => import("@/components/quiz/QuizCompleted.vue");
const QuizSurvey = () => import("@/components/quiz/QuizSurvey.vue");
const QuizSurveyAnswer = () => import("@/components/quiz/QuizSurveyAnswer.vue");
const QuizSurveyBody = () => import("@/components/quiz/QuizSurveyBody.vue");

const FlexView = () => import("@/components/flex/FlexView.vue");
const FlexLessonCompleted = () =>
  import("@/components/flex/LessonCompleted.vue");
const FlexCourseCompleted = () =>
  import("@/components/flex/CourseCompleted.vue");

const FlexEditor = () => import("@/components/flex/FlexEditor.vue");
const FlexTranslations = () => import("@/components/flex/FlexTranslations.vue");

const EventCourse = () => import("@/course/views/EventCourse.vue");

const AssessmentList = () =>
  import("@/components/my-course/AssessmentList.vue");

const CovidView = () => import("@/components/covid/CovidView.vue");

const TestCenterView = () =>
  import("@/components/test-center/TestCenterView.vue");

const PrivacyPolicy = () => import("@/components/auth/DataPrivacyPolicy.vue");
const TermsAndConditions = () =>
  import("@/components/auth/TermsConditions.vue");
const NotFound = () => import("@/components/NotFound.vue");

const AutomationCenter = () =>
  import("@/components/admin/admin-action/AutomationCenter.vue");
const SignUpKeys = () =>
  import("@/components/admin/admin-action/SignUpKeys.vue");
const LoginRedirects = () =>
  import("@/components/admin/admin-action/LoginRedirects.vue");
const Accounts = () => import("@/components/admin/admin-action/Accounts.vue");

const Organization = () =>
  import("@/components/admin/admin-action/Organization.vue");
const AdminProperties = () =>
  import("@/components/admin/admin-action/Properties.vue");
const UserImport = () => import("@/admin/users/views/UserImport.vue");
const AdminAccountAddEdit = () =>
  import("@/components/admin/admin-action/AccountAddEdit.vue");
const AdminAccountImport = () =>
  import("@/components/admin/admin-action/AccountImport.vue");
const AdminAccountStatistic = () =>
  import("@/components/admin/admin-action/AccountStatistics.vue");
const AdminCovid = () =>
  import("@/components/admin/admin-action/CovidAdmin.vue");
const AdminQuestionnaire = () =>
  import("../components/admin/admin-action/AdminQuestionnaire.vue");
const AdminQuestionnaireEdit = () =>
  import("@/components/admin/admin-action/AdminQuestionnaireEdit.vue");

const TestcenterTranslations = () =>
  import("@/components/admin/admin-action/TestcenterTranslations.vue");

const SealIcon = () => import("@/components/icons/SealIcon.vue");

const Logs = () => import("@/components/admin/admin-action/Logs.vue");
const LogSettings = () =>
  import("@/components/admin/admin-action/LogSettings.vue");

const HardwareRestrictions = () =>
  import("@/components/admin/admin-action/HardwareRestriction.vue");
const CovidTranslations = () =>
  import("../components/admin/admin-action/CovidTranslations.vue");

const EmailConfirmation = () =>
  import("@/components/auth/EmailConfirmation.vue");

export const router = createRouter({
  routes: [
    ...adminRoutes,
    ...monitoringRoutes,
    ...courseRoutes,
    ...reportRoutes,
    ...trainerRoutes,

    {
      path: "/",
      name: "Root",
      component: Home,
    },

    {
      path: "/assessmentStatistics",
      name: "AssessmentList",
      component: AssessmentList,
    },
    {
      path: "/tos",
      name: "TermsAndConditions",
      component: TermsAndConditions,
      meta: { public: true },
    },
    {
      path: "/privacy",
      name: "PrivacyPolicy",
      component: PrivacyPolicy,
      meta: { public: true },
    },
    {
      path: "/forgot",
      name: "NewPassword",
      component: NewPassword,
      meta: { public: true },
    },
    {
      path: "/forgot-pass",
      name: "ForgotPassword",
      component: ForgotPassword,
      meta: { public: true },
    },

    {
      path: "/admin/user/import",
      name: "AdminUserImport",
      component: UserImport,
    },

    {
      path: "/admin/properties",
      name: "AdminProperties",
      component: AdminProperties,
    },

    {
      path: "/admin/automation",
      name: "AutomationCenter",
      component: AutomationCenter,
    },
    {
      path: "/admin/organization",
      name: "AdminOrganization",
      component: Organization,
    },
    {
      path: "/admin/account/import",
      name: "AdminAccountImport",
      component: AdminAccountImport,
    },
    {
      path: "/admin/account/edit/:id?",
      name: "AdminAccountAddEdit",
      component: AdminAccountAddEdit,
      props: true,
    },
    {
      path: "/admin/account/statistics",
      name: "AdminAccountStatistic",
      component: AdminAccountStatistic,
    },
    {
      path: "/admin/account",
      name: "AdminAccounts",
      component: Accounts,
    },
    {
      path: "/admin/system",
      name: "AdminSystem",
      component: Logs,
    },
    {
      path: "/admin/hardware",
      name: "HardwareRestrictions",
      component: HardwareRestrictions,
    },
    {
      path: "/admin/system/settings",
      name: "AdminSystemSettings",
      component: LogSettings,
    },
    {
      path: "/admin/questionnaire",
      name: "AdminQuestionnaire",
      component: AdminQuestionnaire,
    },
    {
      path: "/admin/questionnaire/:id",
      name: "AdminQuestionnaireEdit",
      component: AdminQuestionnaireEdit,
      props: true,
    },

    {
      path: "/admin/signup-keys",
      name: "AdminSignUpKeys",
      component: SignUpKeys,
    },
    {
      path: "/admin/signup-keys/login-redirects",
      name: "AdminLoginRedirects",
      component: LoginRedirects,
    },
    {
      path: "/admin/vaccination",
      name: "AdminCovid",
      component: AdminCovid,
    },
    {
      path: "/admin",
      name: "Admin",
      component: Admin,
    },
    {
      path: "/courses-list",
      name: "CoursesList",
      component: CoursesList,
    },
    {
      path: "/test-center",
      name: "TestCenter",
      component: TestCenterView,
    },
    {
      path: "/vaccination",
      name: "Vaccination",
      component: CovidView,
    },
    {
      path: "/program",
      name: "MyCourseList",
      component: MyCourseList,
    },
    {
      path: "/completed",
      name: "CompletedList",
      component: CompletedList,
    },
    {
      path: "/login",
      name: "Login",
      component: Login,
      meta: { public: true },
    },
    {
      path: "/email-confirmation",
      name: "EmailConfirmation",
      component: EmailConfirmation,
      meta: { public: true },
    },
    {
      path: "/registration",
      name: "Registration",
      meta: { public: true },
      component: Registration,
      props: (route) => ({ registrationKey: route.query.key }),
    },
    {
      path: "/registration/:registrationKey",
      name: "RegistrationWithKey",
      meta: { public: true },
      component: Registration,
      props: true,
    },
    {
      path: "/quizelements",
      name: "QuizElements",
      component: QuizElements,
    },
    {
      path: "/quizstart/:quizId",
      name: "QuizStart",
      component: QuizStart,
    },
    {
      path: "/events/:eventId",
      name: "EventCourse",
      component: EventCourse,
      props: true,
    },

    {
      path: "/flex/:flexId/step/:stepId",
      name: "FlexView",
      component: FlexView,
      props: true,
    },
    {
      path: "/flex/:flexId/lesson-completed/:lessonId",
      name: "LessonCompleted",
      component: FlexLessonCompleted,
      props: true,
    },
    {
      path: "/flex/:flexId/course-completed",
      name: "CourseCompleted",
      component: FlexCourseCompleted,
      props: true,
    },
    {
      path: "/flex-editor/:id/",
      name: "FlexEditor",
      component: FlexEditor,
      props: true,
    },
    {
      path: "/flex-translations/:courseId/",
      name: "FlexTranslations",
      component: FlexTranslations,
      props: true,
    },
    {
      path: "/admin/test/translations/",
      name: "TestcenterTranslations",
      component: TestcenterTranslations,
    },

    {
      path: "/vaccination-translations/",
      name: "covidTranslations",
      component: CovidTranslations,
    },
    {
      path: "/quizlesson/:lessonId",
      name: "QuizLesson",
      component: QuizLesson,
    },
    {
      path: "/quizquestion/:lessonId/:stepId",
      name: "QuizQuestion",
      component: QuizQuestion,
    },
    {
      path: "/quizchallenge",
      name: "QuizChallenge",
      component: QuizChallenge,
    },
    {
      path: "/quizsurvey/:lessonId/:stepId",
      name: "QuizSurvey",
      component: QuizSurvey,
    },
    {
      path: "/quizsurveybody",
      name: "QuizSurveyBody",
      component: QuizSurveyBody,
    },
    {
      path: "/quizsurveyanswer",
      name: "QuizSurveyAnswer",
      component: QuizSurveyAnswer,
    },
    // {
    //   path: '/quizquestionexplanation/:lessonId/:stepId',
    //   name: 'QuizQuestionExplanation',
    //   component: QuizQuestionExplanation,
    // },
    {
      path: "/quizlessoncompleted/:lessonId",
      name: "QuizLessonCompleted",
      component: QuizLessonCompleted,
    },
    {
      path: "/quizcompleted/:quizId",
      name: "QuizCompleted",
      component: QuizCompleted,
    },
    {
      path: "/seal/:quizSuccessRate",
      name: "Seal",
      component: SealIcon,
      props: true,
    },
    {
      path: "/preconfig",
      name: "Preconfig",
      component: () => import("@/components/auth/Preconfig.vue"),
      meta: { public: true },
      props: true,
    },
    {
      path: "/:pathMatch(.*)*",
      name: "404",
      meta: { public: true },
      component: NotFound,
    },
  ],
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  },
});

// Login check
router.beforeEach(async (to, from, next) => {
  if (!to.meta?.public) {
    const isLoggedIn =
      store.getters.token !== undefined && store.getters.token !== null;
    if (!isLoggedIn) {
      next("/login?next=" + encodeURIComponent(to.fullPath));
    } else {
      const userSettingsLoadingState =
        store.getters["user/userSettingsLoadingState"];
      if (!userSettingsLoadingState.loaded) {
        await store.dispatch("user/loadUserSettings");
      }
      next();
    }
  } else {
    next();
  }
});
export default router;

router.onError((error, to) => onRouteError(error, to, i18n));

export function onRouteError(error: any, to: Route, i18n: I18n) {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    error.message.includes("error loading dynamically imported module")
  ) {
    ElNotification({
      title: i18n.global?.t("outdatedVersion") ?? "Outdated Version",
      message: h(
        "a",
        {
          href: "javascript:window.location.reload(true)",
        },
        i18n.global?.t("outdatedVersionReloadPage") ??
          "Version outdated, click to reload the page.",
      ),
      type: "warning",
      duration: 0,
    });
  }
}
